'use client';

import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { type ChangeEvent, type ComponentProps } from 'react';
import { DayPicker, type DropdownProps } from 'react-day-picker';

import { cn } from '../utils/cn';

import { buttonVariants } from './button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

export type CalendarProps = ComponentProps<typeof DayPicker>;

/**
 * Calendar primitive wrapping react-day-picker v10.
 *
 * v10 restructured ClassNames keys (caption → month_caption, head_row → weekdays,
 * cell → day, etc.) and replaced IconLeft/IconRight with a single Chevron
 * component that receives an `orientation` prop.
 */
export const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) => (
  <DayPicker
    showOutsideDays={showOutsideDays}
    // `relative` so the absolutely-positioned prev/next chevrons anchor to
    // the calendar root - not the wider PopoverContent (which can include a
    // preset column in range-picker mode). Without this, `left-1`/`right-1`
    // anchor to whatever ancestor happens to be positioned, leaving the
    // chevrons floating in the preset column or popover gutter.
    className={cn('relative p-3', className)}
    classNames={{
      months: 'flex flex-col sm:flex-row gap-4',
      month: 'space-y-4',
      // `px-9` reserves 36px on each side for the absolutely-positioned
      // prev/next chevrons (28px wide, anchored at left-3 / right-3
      // from calendar root). Without this the dropdown caption row
      // grows wide enough to slide under the chevrons.
      month_caption: 'flex justify-center pt-1 relative items-center px-9',
      caption_label: 'text-sm font-medium',
      nav: 'space-x-1 flex items-center',
      // `top-3` pins the chevrons to the caption row baseline (calendar root
      // padding is `p-3`, so `top-3` lines them up with the month label).
      // Full-opacity by default - the previous `opacity-50` read as
      // "disabled" to operators, even though the chevrons were always
      // clickable. Now they look interactive, with a hover bg for the
      // standard affordance.
      // Chevrons styled as borderless ghost icons - the reference
      // design uses bare chevrons (no boxy outline) so they fade
      // into the caption row and let the month/year dropdowns lead
      // visually.
      // `z-20` keeps the chevrons above the month/year dropdowns when
      // `captionLayout="dropdown"` is set - the dropdowns center inside
      // the caption row and were swallowing pointer events at the
      // chevron coords, which read to operators as "the arrows do
      // nothing". Lifting to z-20 (above SelectTrigger's default z) is
      // the actual fix.
      button_previous: cn(
        buttonVariants({ variant: 'ghost' }),
        'text-muted-foreground hover:text-foreground absolute top-3 left-3 z-20 h-7 w-7 p-0 hover:bg-zinc-100',
      ),
      button_next: cn(
        buttonVariants({ variant: 'ghost' }),
        'text-muted-foreground hover:text-foreground absolute top-3 right-3 z-20 h-7 w-7 p-0 hover:bg-zinc-100',
      ),
      // The actual visual styling for the month + year selects lives
      // on the `Dropdown` component override below (Radix Select).
      // We only style the row container here.
      dropdowns: 'flex items-center gap-2',
      dropdown_root: 'relative inline-flex items-center',
      month_grid: 'w-full border-collapse space-y-1',
      weekdays: 'flex',
      // Uppercase, slightly smaller, muted - reads as a header row
      // rather than a row of regular letters.
      weekday: 'text-muted-foreground w-9 font-medium text-[0.7rem] uppercase tracking-wide',
      week: 'flex w-full mt-2',
      day: 'h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20',
      day_button: cn(
        buttonVariants({ variant: 'ghost' }),
        'h-9 w-9 p-0 font-normal aria-selected:opacity-100',
      ),
      selected: 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
      // Today is OUTLINED (ring), not filled - matches the reference
      // design where today reads as "current marker" rather than
      // "currently selected". Selection still wins because `selected`
      // applies `bg-primary` which paints over this ring.
      today: 'rounded-md ring-2 ring-inset ring-primary text-primary font-semibold',
      // Outside-month days stay readable - the reference doesn't fade
      // them to ghost. 50% felt like they were turned off; 70% reads
      // as "secondary but still part of the grid".
      outside: 'text-muted-foreground/70',
      disabled: 'text-muted-foreground/40',
      hidden: 'invisible',
      ...classNames,
    }}
    components={{
      // Handle all four orientations - react-day-picker uses
      // `down` inside MonthsDropdown / YearsDropdown caption labels
      // (and `up` in some configs). Without the down case the
      // dropdown caption rendered a right-chevron next to the month
      // name, which looked like a stray `>` glyph in the header.
      Chevron: ({ orientation }) => {
        if (orientation === 'left') return <ChevronLeft className="h-4 w-4" />;
        if (orientation === 'right') return <ChevronRight className="h-4 w-4" />;
        if (orientation === 'up') return <ChevronUp className="h-3.5 w-3.5" />;
        return <ChevronDown className="h-3.5 w-3.5" />;
      },
      // Override RDP's native-<select>-based Dropdown with the Radix
      // Select used everywhere else in the app. This swaps both
      // MonthsDropdown and YearsDropdown in one shot (both delegate
      // to `components.Dropdown`). Replacing the component also kills
      // RDP's duplicate aria-hidden caption span, so no more "June ›"
      // ghost text next to the trigger.
      Dropdown: CalendarSelectDropdown,
    }}
    {...props}
  />
);
Calendar.displayName = 'Calendar';

/**
 * Radix-Select replacement for react-day-picker's native-<select>
 * dropdown. RDP feeds us a numeric value + a typical onChange that
 * reads `e.target.value` as a string; Radix Select gives us back a
 * string from `onValueChange`. We bridge the two by faking the
 * ChangeEvent shape RDP's handler actually destructures (it only
 * touches `target.value`).
 *
 * Sized to read as a compact pill in the caption row - matches the
 * outline-input language of the rest of the form system.
 */
const CalendarSelectDropdown = ({
  value,
  onChange,
  options,
  disabled,
  'aria-label': ariaLabel,
}: DropdownProps) => (
  <Select
    value={value === undefined ? undefined : String(value)}
    onValueChange={(v) =>
      onChange?.({ target: { value: v } } as unknown as ChangeEvent<HTMLSelectElement>)
    }
    disabled={disabled}
  >
    <SelectTrigger
      aria-label={ariaLabel}
      className="focus:border-ring h-8 w-auto gap-1.5 rounded-md border border-zinc-200 bg-white px-2.5 text-sm font-medium hover:bg-zinc-50"
    >
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="max-h-72">
      {options?.map((o) => (
        <SelectItem key={o.value} value={String(o.value)} disabled={o.disabled}>
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);
